/**
 * Settings platform services for the web/desktop shell (one set per app container): the SettingsApi (real API or the
 * in-browser mock, code-split), the browser's time zones and the image processor stickers use (square crop → 512 px
 * WebP), so profile photos and workspace icons take the same path as sticker images.
 */
import type { SettingsApi, SettingsDeps } from "@bundle/core";
import type { Container } from "../container.ts";
import { importWatch, withImportAnalytics } from "../telemetryApi.ts";
import { createStickerImages } from "../stickers/image.ts";
import { createSettingsApi } from "./api.ts";
import { browserTimeZones } from "./zones.ts";

const byContainer = new WeakMap<Container, SettingsDeps>();

function deferred<T extends object>(target: Promise<T>): T {
  return new Proxy({} as T, {
    get: (_obj, key) => (key === "then" ? undefined : (...args: unknown[]) => target.then((t) => (t[key as keyof T] as (...a: unknown[]) => unknown)(...args))),
  });
}

export function settingsPlatform(container: Container): SettingsDeps {
  const existing = byContainer.get(container);
  if (existing !== undefined) return existing;
  const api: SettingsApi = container.mock
    ? deferred<SettingsApi>(import("../mock/settingsApi.ts").then(({ createMockSettingsApi }) => createMockSettingsApi(() => container.session.state.session?.token)))
    : withImportAnalytics(createSettingsApi(container.session), importWatch(container.analytics));
  const deps: SettingsDeps = { api, zones: browserTimeZones, images: createStickerImages(container.media) };
  byContainer.set(container, deps);
  return deps;
}
